import { History } from 'history';
import lscache from 'lscache';
import { FormEvent } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import { CONTACT_HOST_SOURCES } from '~/constants';
import { useLang } from '~/stores/i18n';

import { InitialFormData, ValidationResults } from '../types/types';
import selectAvailableHost from '../utils/selectAvailableHost';
import { validateInputs } from '../utils/validatePersonalizedOfferInputs';

const FULL_DAY_MINUTES = 60 * 24;

interface PersonalizedOfferFormProps {
  hostId: string;
  experienceId?: string;
  source: CONTACT_HOST_SOURCES;
}

const usePersonalizedOfferForm = ({
  hostId,
  experienceId,
  source,
}: PersonalizedOfferFormProps) => {
  const location = useLocation();
  const lang = useLang();
  const initialFormData: InitialFormData = useMemo(() => {
    const SearchUrlParams = new URLSearchParams(location.search);
    const storedFormData = lscache.get('personalizeOfferForm') || {};

    const initialParams: Record<string, string> = {
      request: '',
      adults: SearchUrlParams?.get('adults') || '4',
      children: SearchUrlParams?.get('children') || '0',
      date: SearchUrlParams?.get('date') || '',
      time: SearchUrlParams?.get('time') || '',
      ...storedFormData,
      hostId,
      experienceId,
    };

    const validation: ValidationResults = validateInputs(initialParams);

    return {
      request: initialParams.request,
      adults: validation.validValues.adults || '4',
      children: validation.validValues.children || '0',
      date: validation.validValues.date || '',
      time: validation.validValues.time || '',
      hostId,
      experienceId,
    };
  }, [location, hostId, experienceId]);

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
    isLoggedIn: boolean,
    history: History,
    pathname: string,
  ): Promise<{ successful: boolean; errors?: Record<string, string> }> => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);

    const submittedData: Record<string, string> = {
      hostId,
      experienceId,
      request: formData.get('request') as string,
      adults: formData.get('adults') as string,
      children: formData.get('children') as string,
      date: formData.get('date') as string,
      time: formData.get('time') as string,
      duration: formData.get('duration') as string,
    };

    const validation: ValidationResults = validateInputs(submittedData);
    if (!validation.valid) {
      console.error('Form submission contains errors:', validation.errors);
      return { successful: false, errors: validation.errors };
    }

    let selectedHostId: string = hostId;
    if (source === CONTACT_HOST_SOURCES.EXPERIENCE_PAGE) {
      selectedHostId = await selectAvailableHost({
        experienceId,
        date: submittedData.date,
        time: submittedData.time,
        preferredHostId: hostId,
      });
    }

    if (selectedHostId !== hostId) {
      submittedData.hostId = selectedHostId;
    }

    const updatedPathname =
      selectedHostId !== hostId
        ? pathname.replace(hostId, selectedHostId)
        : pathname;

    lscache.set('personalizeOfferForm', { ...submittedData }, FULL_DAY_MINUTES);

    if (!isLoggedIn) {
      const searchParams = new URLSearchParams(location.search);
      const returnTo =
        window.location.origin +
        generatePathnameWithLangCode(`${updatedPathname}sending/`, lang);
      searchParams.set('returnTo', returnTo);
      history.push({
        pathname: generatePathnameWithLangCode('/signup/', lang),
        state: { modal: true },
        search: searchParams.toString(),
      });
      return { successful: true };
    }

    history.replace(`${updatedPathname}sending/`);
    return { successful: true };
  };

  return {
    initialFormData,
    handleSubmit,
  };
};

export default usePersonalizedOfferForm;
